
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { PageSkeleton } from '@/components/ui/page-skeleton';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { PlatformProtectedRoute } from '@/components/auth/PlatformProtectedRoute';
import { AdminLayout } from '@/components/admin/AdminLayout';
import GuestApp from '@/pages/guest';

// Public pages
const Index = lazy(() => import('@/pages/Index'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const PrivacyPage = lazy(() => import('@/pages/Privacy'));
const TermsPage = lazy(() => import('@/pages/Terms'));
const ButtonTester = lazy(() => import('@/pages/button-tester'));
const RegisterHotelPage = lazy(() => import('@/pages/register-hotel'));
const TestAuthPage = lazy(() => import('@/pages/test-auth'));
const AuthPage = lazy(() => import('@/pages/auth'));

// Lazy load admin components
const AdminDashboard = lazy(() => import('@/pages/admin/dashboard'));
const AdminRoomsPage = lazy(() => import('@/pages/admin/rooms'));
const HousekeepingMobilePage = lazy(() => import('@/pages/admin/housekeeping-mobile'));
const HousekeepingInspectorPage = lazy(() => import('@/pages/admin/housekeeping-inspector'));
const AdminGuestsPage = lazy(() => import('@/pages/admin/guests'));
const AdminServicesPage = lazy(() => import('@/pages/admin/services'));
const AdminSettingsPage = lazy(() => import('@/pages/admin/settings'));
const AdminUsersPage = lazy(() => import('@/pages/admin/users'));
const AdminNotificationsPage = lazy(() => import('@/pages/admin/notifications'));
const AdminAnalyticsPage = lazy(() => import('@/pages/admin/analytics'));
const AdminIntegrationsPage = lazy(() => import('@/pages/admin/integrations'));
const AdminMigrationPage = lazy(() => import('@/pages/admin/migration'));
const AdminMediaPage = lazy(() => import('@/pages/admin/media'));
const AdminAuditPage = lazy(() => import('@/pages/admin/audit'));
const AdminBotPage = lazy(() => import('@/pages/admin/bot'));
const AdminAIMarketingPage = lazy(() => import('@/pages/admin/ai-marketing'));
const AdminAIGuestPage = lazy(() => import('@/pages/admin/ai-guest'));
const AdminAIRevenueManagerPage = lazy(() => import('@/pages/admin/ai-revenue-manager'));
const AdminAIOperationsPage = lazy(() => import('@/pages/admin/ai-operations'));
const AdminAIAnalyticsPage = lazy(() => import('@/pages/admin/ai-analytics'));
const AdminAIAssistantPage = lazy(() => import('@/pages/admin/ai-assistant'));
const AdminRoomGridPage = lazy(() => import('@/pages/admin/room-grid'));
const AdminRoomGridLitePage = lazy(() => import('@/pages/admin/room-grid-lite'));
const SelectelIntegrationPage = lazy(() => import("@/pages/admin/selectel-integration"));
const SelectelHealthPage = lazy(() => import("@/pages/admin/selectel-health"));
const GenerateDemoDataPage = lazy(() => import("@/pages/admin/generate-demo-data"));

// Missing admin pages
const AdminDeletedBookingsPage = lazy(() => import('@/pages/admin/AdminDeletedBookingsPage'));
const BookingsListPage = lazy(() => import('@/pages/admin/bookings-list'));
const CancelledBookingsListPage = lazy(() => import('@/pages/admin/bookings-list-otmena'));
const GuestManagementPage = lazy(() => import('@/pages/admin/guest-management'));
const RestrictionsCalendarPage = lazy(() => import('@/pages/admin/restrictions-calendar'));
const BotsPage = lazy(() => import('@/pages/admin/bots'));
const AdminConfigPage = lazy(() => import('@/pages/admin/config'));
const AdminContentPage = lazy(() => import('@/pages/admin/content'));
const AdminOnboardingPage = lazy(() => import('@/pages/admin/onboarding'));
const HotelSwitcherPage = lazy(() => import('@/pages/admin/HotelSwitcherPage'));
const ChannelManagerPage = lazy(() => import('@/pages/admin/channel-manager'));
const AdminOffersPage = lazy(() => import('@/pages/admin/offers'));
const AdminMicePage = lazy(() => import('@/pages/admin/mice'));
const AdminMealsPage = lazy(() => import('@/pages/admin/meals'));
const MealPricesPage = lazy(() => import('@/pages/admin/MealPricesPage'));

// Public MICE page
const MiceCalculatorPage = lazy(() => import('@/pages/MiceCalculatorPage'));

const OnlineWidgetRedirect = () => {
  const { hotelId } = useParams();

  React.useEffect(() => {
    const slug = hotelId?.trim();
    const query = window.location.search || '';
    const target = slug
      ? `/widget.html#/booking/${encodeURIComponent(slug)}${query}`
      : `/widget.html#/booking${query}`;
    window.location.replace(target);
  }, [hotelId]);

  return <PageSkeleton />;
};

// Oscar Booking Page - специальный шаблон для Оскара
const OscarBookingPage = lazy(() => import('@/pages/OscarBookingPage'));

// Cache reset page
const CacheResetPage = lazy(() => import('@/pages/CacheResetPage'));
 
 // Booking Status page (public - for QR code scanning)
 const BookingStatusPage = lazy(() => import('@/pages/BookingStatusPage'));

// AI Specialists pages
const AdminAIFinancePage = lazy(() => import('@/pages/admin/ai-finance'));
const AIFnBPage = lazy(() => import('@/pages/admin/ai-fnb'));
const AIHRPage = lazy(() => import('@/pages/admin/ai-hr'));
const AdminAIMarketPage = lazy(() => import('@/pages/admin/ai-market'));
const AdminAIReputationPage = lazy(() => import('@/pages/admin/ai-reputation'));
const AdminAIRoomGridPage = lazy(() => import('@/pages/admin/ai-room-grid'));
const AISecurityPage = lazy(() => import('@/pages/admin/ai-security'));
const AdminAIStrategicPage = lazy(() => import('@/pages/admin/ai-strategic'));
const AISustainabilityPage = lazy(() => import('@/pages/admin/ai-sustainability'));
const MarketIntelligenceTestPage = lazy(() => import('@/pages/admin/market-intelligence-test'));
const MarketIntelligencePage = lazy(() => import('@/pages/admin/market-intelligence'));
const CompetitorDetailPage = lazy(() => import('@/pages/admin/competitor-detail'));
const RoomCategoryDetailPage = lazy(() => import('@/pages/admin/room-category-detail'));

// Platform pages
const PlatformAdminPage = lazy(() => import('@/pages/platform/admin'));
const AddHotelPage = lazy(() => import('@/pages/platform/add-hotel'));
const PlatformLoginPage = lazy(() => import('@/pages/platform/login'));

// Subscription pages
const SubscriptionPaymentPage = lazy(() => import('@/pages/subscription/payment'));
const SubscriptionSuccessPage = lazy(() => import('@/pages/subscription/success'));

// Public pages
const AnalyticsPage = lazy(() => import('@/pages/analytics'));
const ContentPage = lazy(() => import('@/pages/content'));
const MediaPage = lazy(() => import('@/pages/media'));
const AIAssistantPage = lazy(() => import('@/pages/ai-assistant'));
const StRegisAbuDhabiHomePage = lazy(() => import('@/pages/hotel/index'));
const NellyHotelPage = lazy(() => import('@/pages/hotel/nelly'));
const NellyNewPage = lazy(() => import('@/pages/hotel/nelly-new'));
const HotelBazaPage = lazy(() => import('@/pages/hotelbaza'));
const AISpecialists = lazy(() => import('@/pages/AISpecialists'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const StrategicAdvisor = lazy(() => import('@/pages/ai/StrategicAdvisor'));
const HotelManagement = lazy(() => import('@/pages/HotelManagement'));
const MobileAppsPage = lazy(() => import('@/pages/mobile-apps'));
const SmallHotelsPage = lazy(() => import('@/pages/solutions/small-hotels'));
const HotelChainsPage = lazy(() => import('@/pages/solutions/hotel-chains'));
const ResortsPage = lazy(() => import('@/pages/solutions/resorts'));
const BusinessHotelsPage = lazy(() => import('@/pages/solutions/business-hotels'));
const ApartmentsPage = lazy(() => import('@/pages/solutions/apartments'));
const HotelsPage = lazy(() => import('@/pages/solutions/hotels'));

// Integration pages
const OstrovokIntegrationPage = lazy(() => import('@/pages/integrations/ostrovok'));
const ExpediaIntegrationPage = lazy(() => import('@/pages/integrations/expedia'));
const OTAIntegrationPage = lazy(() => import('@/pages/integrations/ota'));
const PaymentsIntegrationPage = lazy(() => import('@/pages/integrations/payments'));
const CRMIntegrationPage = lazy(() => import('@/pages/integrations/crm'));
const MarketplaceIntegrationPage = lazy(() => import('@/pages/integrations/marketplace'));

// AI pages  
const AIMarketPage = lazy(() => import('@/pages/ai/market'));
const AIFinancePage = lazy(() => import('@/pages/ai/finance'));
const AIOperationsPage = lazy(() => import('@/pages/ai/operations'));
const AIReputationPage = lazy(() => import('@/pages/ai/reputation'));
const AIGuestPage = lazy(() => import('@/pages/ai/guest'));
const AIRevenuePage = lazy(() => import('@/pages/ai/revenue'));
const AIChannelPage = lazy(() => import('@/pages/ai/channel'));
const AICommunicationPage = lazy(() => import('@/pages/ai/communication'));
const AIAutomationPage = lazy(() => import('@/pages/ai/automation'));
const AIConciergeePage = lazy(() => import('@/pages/ai/concierge'));
const AISecurityPagePublic = lazy(() => import('@/pages/ai/security'));

// Resources pages
const TrainingPage = lazy(() => import('@/pages/resources/training'));
const APIPage = lazy(() => import('@/pages/resources/api'));
const VideosPage = lazy(() => import('@/pages/resources/videos'));
const SupportPage = lazy(() => import('@/pages/resources/support'));

// Admin Routes Component
const AdminRoutes = () => (
  <Routes>
    {/* Dashboard */}
    <Route path="dashboard" element={<AdminDashboard />} />
    
    {/* Core admin routes */}
    <Route path="bookings" element={<Navigate to="/admin/bookings-list" replace />} />
    <Route path="bookings-list" element={<BookingsListPage />} />
    <Route path="bookings-list-otmena" element={<CancelledBookingsListPage />} />
    <Route path="meals" element={<AdminMealsPage />} />
    <Route path="meal-prices" element={<MealPricesPage />} />
    <Route path="deleted-bookings" element={<AdminDeletedBookingsPage />} />
    <Route path="rooms" element={<AdminRoomsPage />} />
    <Route path="housekeeping-mobile" element={<HousekeepingMobilePage />} />
    <Route path="housekeeping-inspector" element={<HousekeepingInspectorPage />} />
    <Route path="room-grid" element={<AdminRoomGridPage />} />
    <Route path="room-grid-lite" element={<AdminRoomGridLitePage />} />
    <Route path="guests" element={<AdminGuestsPage />} />
    <Route path="guest-management" element={<GuestManagementPage />} />
    <Route path="restrictions-calendar" element={<RestrictionsCalendarPage />} />
    <Route path="services" element={<AdminServicesPage />} />
    <Route path="settings" element={<AdminSettingsPage />} />
    <Route path="users" element={<AdminUsersPage />} />
    <Route path="notifications" element={<AdminNotificationsPage />} />
    <Route path="analytics" element={<AdminAnalyticsPage />} />
    <Route path="integrations" element={<AdminIntegrationsPage />} />
    <Route path="migration" element={<AdminMigrationPage />} />
    <Route path="import" element={<AdminMigrationPage />} />
    <Route path="media" element={<AdminMediaPage />} />
    <Route path="audit" element={<AdminAuditPage />} />
    <Route path="bot" element={<AdminBotPage />} />
    <Route path="bots" element={<BotsPage />} />
    <Route path="config" element={<AdminConfigPage />} />
    <Route path="content" element={<AdminContentPage />} />
    <Route path="onboarding" element={<AdminOnboardingPage />} />
    <Route path="offers" element={<AdminOffersPage />} />
    <Route path="channel-manager" element={<ChannelManagerPage />} />
    <Route path="hotel-switcher" element={<HotelSwitcherPage />} />
    <Route path="mice" element={<AdminMicePage />} />
    
    {/* AI Specialists */}
    <Route path="ai-marketing" element={<AdminAIMarketingPage />} />
    <Route path="ai-guest" element={<AdminAIGuestPage />} />
    <Route path="ai-revenue-manager" element={<AdminAIRevenueManagerPage />} />
    <Route path="ai-operations" element={<AdminAIOperationsPage />} />
    <Route path="ai-analytics" element={<AdminAIAnalyticsPage />} />
    <Route path="ai-assistant" element={<AdminAIAssistantPage />} />
    <Route path="ai-finance" element={<AdminAIFinancePage />} />
    <Route path="ai-fnb" element={<AIFnBPage />} />
    <Route path="ai-hr" element={<AIHRPage />} />
    <Route path="ai-market" element={<AdminAIMarketPage />} />
    <Route path="ai-reputation" element={<AdminAIReputationPage />} />
    <Route path="ai-room-grid" element={<AdminAIRoomGridPage />} />
    <Route path="ai-security" element={<AISecurityPage />} />
    <Route path="ai-strategic" element={<AdminAIStrategicPage />} />
    <Route path="ai-sustainability" element={<AISustainabilityPage />} />
    
    {/* Market Intelligence Test Page */}
    <Route path="market-intelligence-test" element={<MarketIntelligenceTestPage />} />
    
    {/* Market Intelligence - детальная страница конкурента */}
    <Route path="market-intelligence/competitor/:competitorId" element={<CompetitorDetailPage />} />
    
    {/* Market Intelligence - детальная страница категории */}
    <Route path="market-intelligence/category/:categoryId" element={<RoomCategoryDetailPage />} />
    
    {/* Market Intelligence - параметризованный маршрут для мультитенантности */}
    <Route path="market-intelligence/:hotelSlug?" element={<MarketIntelligencePage />} />
    
    {/* Selectel integration routes */}
    <Route path="selectel-integration" element={<SelectelIntegrationPage />} />
    <Route path="selectel-health" element={<SelectelHealthPage />} />
    
    {/* Demo data generation */}
    <Route path="generate-demo-data" element={<GenerateDemoDataPage />} />
    
    {/* Redirect admin root to bookings-list (список бронирований - главная страница после авторизации) */}
    <Route path="" element={<Navigate to="bookings-list" replace />} />
    <Route path="*" element={<Navigate to="bookings-list" replace />} />
  </Routes>
);

export const HotelRouter = () => {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
        {/* Main public page */}
        <Route path="/" element={<Index />} />
        
        {/* Legal pages */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        
        {/* Cache Reset - hidden utility page */}
        <Route path="/reset" element={<CacheResetPage />} />
        
        {/* MICE Calculator */}
        <Route path="/mice" element={<MiceCalculatorPage />} />
        
        {/* Public widget routes now redirect to the autonomous online runtime */}
        <Route path="/widget/:hotelId" element={<OnlineWidgetRedirect />} />
        
         {/* Booking Status - public page for QR code scanning */}
         <Route path="/booking/status/:reference" element={<BookingStatusPage />} />
        
        {/* Booking routes - all use new widget */}
        <Route path="/booking/:hotelId" element={<OnlineWidgetRedirect />} />
        <Route path="/booking" element={<OnlineWidgetRedirect />} />
        
        {/* Oscar Booking Page - специальный шаблон для Оскара */}
        <Route path="/b/:hotelId" element={<OscarBookingPage />} />
        <Route path="/button-tester" element={<ButtonTester />} />
        <Route path="/register-hotel" element={<RegisterHotelPage />} />
        <Route path="/test-auth" element={<TestAuthPage />} />
        <Route path="/guest" element={<GuestApp />} />
        <Route path="/guest/*" element={<GuestApp />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/ai-specialists" element={<AISpecialists />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/ai/strategic" element={<StrategicAdvisor />} />
        <Route path="/hotel-management" element={<HotelManagement />} />
        <Route path="/mobile-apps" element={<MobileAppsPage />} />
        <Route path="/solutions/small-hotels" element={<SmallHotelsPage />} />
        <Route path="/solutions/hotel-chains" element={<HotelChainsPage />} />
        <Route path="/solutions/resorts" element={<ResortsPage />} />
        <Route path="/solutions/business-hotels" element={<BusinessHotelsPage />} />
        <Route path="/solutions/apartments" element={<ApartmentsPage />} />
        <Route path="/solutions/hotels" element={<HotelsPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/content" element={<ContentPage />} />
        <Route path="/media" element={<MediaPage />} />
        <Route path="/ai-assistant" element={<AIAssistantPage />} />
        <Route path="/hotel" element={<StRegisAbuDhabiHomePage />} />
        <Route path="/hotel/nelly" element={<NellyHotelPage />} />
        <Route path="/hotel/nelly-new" element={<NellyNewPage />} />
        <Route path="/hotelbaza" element={<HotelBazaPage />} />
        <Route path="/hotelbaza/:hotelId" element={<HotelBazaPage />} />
        
        {/* Integration routes */}
        <Route path="/integrations/ostrovok" element={<OstrovokIntegrationPage />} />
        <Route path="/integrations/expedia" element={<ExpediaIntegrationPage />} />
        <Route path="/integrations/ota" element={<OTAIntegrationPage />} />
        <Route path="/integrations/payments" element={<PaymentsIntegrationPage />} />
        <Route path="/integrations/crm" element={<CRMIntegrationPage />} />
        <Route path="/integrations/marketplace" element={<MarketplaceIntegrationPage />} />
        
        {/* AI routes */}
        <Route path="/ai/market" element={<AIMarketPage />} />
        <Route path="/ai/finance" element={<AIFinancePage />} />
        <Route path="/ai/operations" element={<AIOperationsPage />} />
        <Route path="/ai/reputation" element={<AIReputationPage />} />
        <Route path="/ai/guest" element={<AIGuestPage />} />
        <Route path="/ai/revenue" element={<AIRevenuePage />} />
        <Route path="/ai/channel" element={<AIChannelPage />} />
        <Route path="/ai/communication" element={<AICommunicationPage />} />
        <Route path="/ai/automation" element={<AIAutomationPage />} />
        <Route path="/ai/concierge" element={<AIConciergeePage />} />
        <Route path="/ai/security" element={<AISecurityPagePublic />} />
        
        {/* Resources routes */}
        <Route path="/resources/training" element={<TrainingPage />} />
        <Route path="/resources/api" element={<APIPage />} />
        <Route path="/resources/videos" element={<VideosPage />} />
        <Route path="/resources/support" element={<SupportPage />} />
        
        {/* Platform routes */}
        <Route path="/platform/login" element={<PlatformLoginPage />} />
        <Route
          path="/platform/admin"
          element={
            <PlatformProtectedRoute>
              <PlatformAdminPage />
            </PlatformProtectedRoute>
          }
        />
        <Route
          path="/platform/add-hotel"
          element={
            <PlatformProtectedRoute>
              <AddHotelPage />
          </PlatformProtectedRoute>
          }
        />
        
        {/* Subscription payment pages */}
        <Route
          path="/subscription/payment"
          element={
            <ProtectedRoute>
              <SubscriptionPaymentPage />
            </ProtectedRoute>
          }
        />
        <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
        
        {/* Protected admin routes */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <AdminRoutes />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        
        {/* 404 catch-all route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};
